import { can } from '@barso/shared';
import { useEffect, useState } from 'react';
import { Navigate, useSearchParams } from 'react-router';
import { helperApi, HELPER_EVENT_ROLE_LABELS, type HelperEvent } from '../api/endpoints';
import { messageFor } from '../api/messages';
import { HelpLink } from '../components/HelpLink';
import { Alert, EmptyState, LinkButton, Tile, TileGroup } from '../components/ui';
import { dateTime, STATUS_LABELS } from '../format';
import { lastPosEvents } from '../pos/sessions';

/** Basare, in denen die Person kassieren darf (Recht `event.sales.create`, aus `permissions.ts`), nur laufende (Server-Filter). */
export function registerEvents(events: readonly HelperEvent[]): HelperEvent[] {
  return events.filter((event) =>
    can({ orgRoles: [], eventRoles: event.roles }, 'event.sales.create'),
  );
}

/**
 * Hinweis, warum eine Kasse nicht (mehr) öffnet. Kassieren geht nur im Verkauf; in der Annahme lässt sich die Kasse schon
 * einrichten und das Gerät prüfen (Kassenplatz wählen, Artikel laden), darum bleibt sie dort erreichbar.
 */
function closedReason(status: HelperEvent['status']): string | undefined {
  return status === 'returns' ? 'Der Verkauf ist beendet' : undefined;
}

function stateHint(status: HelperEvent['status']): string | undefined {
  return status === 'intake'
    ? 'Jetzt einrichten und Gerät prüfen, kassieren ab Verkaufsbeginn'
    : undefined;
}

function eventMeta(event: HelperEvent): string {
  const roles = event.roles.map((role) => HELPER_EVENT_ROLE_LABELS[role]).join(', ');
  const start = event.startsAt === null ? '' : ` · ${dateTime(event.startsAt)}`;
  return `${event.organizationName} · ${STATUS_LABELS[event.status]}${start} · ${roles}`;
}

/**
 * Einstieg mit einem Tipp (`/kasse`, auch als App-Verknüpfung): führt zur zuletzt benutzten Kasse dieses Geräts, die
 * Auswahl kommt aus dem lokalen Speicher und geht auch ohne Netz. Ohne gemerkte Kasse (oder mit `?alle=1`) steht
 * „Ihre Kassen“ da, nie eine Sackgasse: Ohne Kasse erklärt die Seite, was zu tun ist, und bietet die Kopplung per QR an.
 */
export function QuickRegisterPage() {
  const [params] = useSearchParams();
  const last = lastPosEvents()[0];
  if (last !== undefined && params.get('alle') === null) {
    // Kasse mit Schnellzugang (E11): die Geräte-Seite braucht keine Anmeldung
    const target =
      last.device === true ? `/kasse-geraet/${last.eventId}` : `/basare/${last.eventId}/kasse`;
    return <Navigate to={target} replace />;
  }
  return <RegisterList />;
}

function RegisterList() {
  const [events, setEvents] = useState<HelperEvent[] | null>(null);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    const guard = { cancelled: false };
    helperApi.myEvents().then(
      (loaded) => {
        if (!guard.cancelled) setEvents(registerEvents(loaded));
      },
      (caught: unknown) => {
        if (!guard.cancelled) setError(messageFor(caught));
      },
    );
    return () => {
      guard.cancelled = true;
    };
  }, []);

  const pair = (
    <LinkButton variant="secondary" to="/kasse-scannen">
      Kasse per QR koppeln
    </LinkButton>
  );
  return (
    <div className="stack stack--lg">
      <h1>Kasse</h1>
      <p className="muted">
        <HelpLink slug="kasse-bedienen" />
      </p>
      {error !== null && (
        <Alert kind="error">
          {error} Ohne Netz geht es mit einer schon geöffneten Kasse weiter, sonst bitte später
          erneut versuchen.
        </Alert>
      )}
      {events === null ? (
        error === null && <p role="status">Einen Moment bitte …</p>
      ) : events.length === 0 ? (
        <EmptyState title="Zurzeit ist keine Kasse für Sie bereit" action={pair}>
          Eine Kasse erscheint hier, sobald der Veranstalter Sie als Kasse eingetragen hat und der
          Basar läuft. Oder Sie koppeln dieses Gerät mit dem QR-Code Ihres Kassenplatzes.
        </EmptyState>
      ) : (
        <>
          <TileGroup title="Ihre Kassen">
            {events.map((event) => {
              const reason = closedReason(event.status);
              const hint = stateHint(event.status);
              return (
                <Tile
                  key={event.eventId}
                  to={`/basare/${event.eventId}/kasse`}
                  icon="euro"
                  title={event.title}
                  description={eventMeta(event)}
                  {...(hint === undefined ? {} : { status: hint })}
                  {...(reason === undefined ? {} : { disabled: reason })}
                />
              );
            })}
          </TileGroup>
          <div className="row">{pair}</div>
        </>
      )}
      {error !== null && <div className="row">{pair}</div>}
    </div>
  );
}

/**
 * Karte „Kasse öffnen · Basar“ für die Startseite: nur, wenn die Person in einem laufenden Verkauf kassieren darf
 * und dieses Gerät nicht schon über „Kasse fortsetzen“ dorthin führt. Fehler bleiben still, die Startseite trägt weiter.
 */
export function OpenRegisterCards() {
  const [events, setEvents] = useState<HelperEvent[]>([]);
  useEffect(() => {
    const guard = { cancelled: false };
    helperApi.myEvents().then(
      (loaded) => {
        if (!guard.cancelled) {
          setEvents(registerEvents(loaded).filter((event) => event.status === 'selling'));
        }
      },
      () => undefined,
    );
    return () => {
      guard.cancelled = true;
    };
  }, []);
  const last = lastPosEvents()[0];
  const shown = events.filter((event) => event.eventId !== last?.eventId);
  if (shown.length === 0) return null;
  return (
    <TileGroup>
      {shown.map((event) => (
        <Tile
          key={event.eventId}
          to={`/basare/${event.eventId}/kasse`}
          icon="euro"
          title={`Kasse öffnen: ${event.title}`}
          description="Der Verkauf läuft, Sie sind als Kasse eingetragen"
        />
      ))}
    </TileGroup>
  );
}
